import { useEffect, useState } from 'react';
import { Button, CenterScreenModal } from '../atoms';
import { useUserLanguage } from '@/customHooks';
import {
  NormalFormNumberField,
  NormalFormSelectionField,
  NormalFormTextField,
} from '../molecules';
import type {
  InventoryFormModalTexts,
  InventoryItemRow,
  InventoryInspectionItemStatus,
} from '@/custom-types';
import textsData from '@/textData/InventoryItemsTableTexts.json';

type InventoryItemsTableProps = {
  items: InventoryItemRow[];
  onChange: (items: InventoryItemRow[]) => void;
  texts: InventoryFormModalTexts;
  isReadOnly: boolean;
};

export const InventoryItemsTable = ({ items, onChange, texts, isReadOnly }: InventoryItemsTableProps) => {
  const userLanguage = useUserLanguage();
  const localTexts = userLanguage === 'es' ? textsData.es : textsData.en;
  const fileTexts =
    userLanguage === 'es'
      ? {
          photos: 'Fotos',
          addPhotos: 'Agregar fotos',
          noPhotos: 'Sin fotos',
          remove: 'Quitar',
          clearAll: 'Limpiar fotos',
          attachedFiles: 'Archivos adjuntos',
          tapToExpand: 'Tocar para ampliar',
        }
      : {
          photos: 'Photos',
          addPhotos: 'Add photos',
          noPhotos: 'No photos',
          remove: 'Remove',
          clearAll: 'Clear photos',
          attachedFiles: 'Attached files',
          tapToExpand: 'Tap to enlarge',
        };
  const [newFilePreviewUrls, setNewFilePreviewUrls] = useState<Record<string, string[]>>({});
  const [viewerState, setViewerState] = useState<{
    isOpen: boolean;
    urls: string[];
    selectedIndex: number;
  }>({
    isOpen: false,
    urls: [],
    selectedIndex: 0,
  });

  useEffect(() => {
    const createdUrls: string[] = [];
    const nextPreviewMap: Record<string, string[]> = {};

    items.forEach((item, index) => {
      const key = item.id ?? `row-${index}`;
      const urls = (item.newFiles ?? []).map((file) => {
        const url = URL.createObjectURL(file);
        createdUrls.push(url);
        return url;
      });
      nextPreviewMap[key] = urls;
    });

    setNewFilePreviewUrls(nextPreviewMap);

    return () => {
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [items]);

  const handleItemChange = (index: number, field: keyof InventoryItemRow, value: string) => {
    onChange(
      items.map((item, idx) =>
        idx === index
          ? {
              ...item,
              [field]: field === 'quantity' ? (value === '' ? '' : Number(value)) : value,
            }
          : item,
      ),
    );
  };

  const handleStatusChange = (index: number, status: InventoryInspectionItemStatus) => {
    onChange(
      items.map((item, idx) => (idx === index ? { ...item, status } : item)),
    );
  };

  const handleAddItem = () => {
    if (isReadOnly) return;
    onChange([
      ...items,
      {
        description: '',
        quantity: '',
        status: 'NOT_EVALUATED',
        notes: '',
        existingFiles: [],
        newFiles: [],
        shouldReplaceFiles: false,
      },
    ]);
  };

  const handleFilesSelected = (index: number, files: FileList | null) => {
    if (!files || files.length === 0 || isReadOnly) return;
    const selectedFiles = Array.from(files);
    onChange(
      items.map((item, idx) =>
        idx === index
          ? {
              ...item,
              newFiles: [...(item.newFiles ?? []), ...selectedFiles],
              shouldReplaceFiles: true,
            }
          : item,
      ),
    );
  };

  const handleRemoveExistingFile = (index: number, fileId: string) => {
    if (isReadOnly) return;
    onChange(
      items.map((item, idx) =>
        idx === index
          ? {
              ...item,
              existingFiles: (item.existingFiles ?? []).filter((file) => file.id !== fileId),
              shouldReplaceFiles: true,
            }
          : item,
      ),
    );
  };

  const handleRemoveNewFile = (index: number, fileIndex: number) => {
    if (isReadOnly) return;
    onChange(
      items.map((item, idx) =>
        idx === index
          ? {
              ...item,
              newFiles: (item.newFiles ?? []).filter((_, newIdx) => newIdx !== fileIndex),
              shouldReplaceFiles: true,
            }
          : item,
      ),
    );
  };

  const handleClearAllFiles = (index: number) => {
    if (isReadOnly) return;
    onChange(
      items.map((item, idx) =>
        idx === index
          ? {
              ...item,
              existingFiles: [],
              newFiles: [],
              shouldReplaceFiles: true,
            }
          : item,
      ),
    );
  };

  const openImageViewer = (item: InventoryItemRow, index: number, selectedIndex: number) => {
    const key = item.id ?? `row-${index}`;
    const urls = [
      ...(item.existingFiles ?? []).map((file) => file.url),
      ...(newFilePreviewUrls[key] ?? []),
    ];

    if (urls.length === 0) return;

    setViewerState({
      isOpen: true,
      urls,
      selectedIndex,
    });
  };

  return (
    <>
      <div className="space-y-3">
      {items.map((item, index) => (
        <div key={item.id ?? index} className="rounded-md border border-gray-200 p-3">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,0.5fr)_minmax(0,1fr)_minmax(0,2fr)_minmax(0,2fr)]">
          <div>
            <NormalFormNumberField
              label={texts.labels.quantity}
              name={`inventoryItemQuantity-${index}`}
              onChange={(value) => handleItemChange(index, 'quantity', value ?? '')}
              value={item.quantity === '' ? '' : String(item.quantity)}
              readOnly={isReadOnly}
            />
          </div>
          <div>
            <NormalFormSelectionField<InventoryInspectionItemStatus>
              label={localTexts.qualityLabel}
              name={`inventoryItemStatus-${index}`}
              options={[
                { name: localTexts.statusOptions.EXCELLENT, value: 'EXCELLENT' },
                { name: localTexts.statusOptions.GOOD, value: 'GOOD' },
                { name: localTexts.statusOptions.REGULAR, value: 'REGULAR' },
                { name: localTexts.statusOptions.BAD, value: 'BAD' },
                { name: localTexts.statusOptions.MISSING, value: 'MISSING' },
                { name: localTexts.statusOptions.NOT_EVALUATED, value: 'NOT_EVALUATED' },
              ]}
              readOnly={isReadOnly}
              value={item.status ?? 'NOT_EVALUATED'}
              onChange={(value) => handleStatusChange(index, value)}
            />
          </div>
          <div>
            <NormalFormTextField
              label={texts.labels.itemDescription}
              name={`inventoryItemDescription-${index}`}
              onChange={(value) => handleItemChange(index, 'description', value ?? '')}
              value={item.description}
              readOnly={isReadOnly}
            />
          </div>
          <div>
            <NormalFormTextField
              label={texts.labels.itemNotes}
              name={`inventoryItemNotes-${index}`}
              onChange={(value) => handleItemChange(index, 'notes', value ?? '')}
              value={item.notes ?? ''}
              readOnly={isReadOnly}
            />
          </div>
          </div>
          <div className="mt-3 space-y-2">
            <p className="text-sm font-medium">{fileTexts.photos}</p>
            {(item.existingFiles ?? []).length === 0 && (item.newFiles ?? []).length === 0 && (
              <p className="text-sm text-gray-500">{fileTexts.noPhotos}</p>
            )}

            {(item.existingFiles ?? []).map((file) => (
              <div key={file.id} className="rounded bg-gray-50 p-2">
                <button
                  type="button"
                  className="w-full text-left"
                  onClick={() =>
                    openImageViewer(
                      item,
                      index,
                      (item.existingFiles ?? []).findIndex((f) => f.id === file.id),
                    )
                  }
                >
                  <img
                    src={file.url}
                    alt={`${fileTexts.attachedFiles} ${file.id}`}
                    className="h-24 w-24 rounded object-cover"
                  />
                </button>
                <p className="mt-1 text-xs text-gray-500">{fileTexts.tapToExpand}</p>
                {!isReadOnly && (
                  <Button secondary onClick={() => handleRemoveExistingFile(index, file.id)}>
                    {fileTexts.remove}
                  </Button>
                )}
              </div>
            ))}

            {(item.newFiles ?? []).map((file, fileIndex) => {
              const key = item.id ?? `row-${index}`;
              const previewUrl = newFilePreviewUrls[key]?.[fileIndex];
              const selectedIndex = (item.existingFiles ?? []).length + fileIndex;

              return (
                <div key={`${file.name}-${fileIndex}`} className="rounded bg-gray-50 p-2">
                  {previewUrl ? (
                    <button type="button" className="w-full text-left" onClick={() => openImageViewer(item, index, selectedIndex)}>
                      <img src={previewUrl} alt={file.name} className="h-24 w-24 rounded object-cover" />
                    </button>
                  ) : (
                    <span className="truncate text-sm">{file.name}</span>
                  )}
                  <p className="mt-1 text-xs text-gray-500">{fileTexts.tapToExpand}</p>
                  {!isReadOnly && (
                    <Button secondary onClick={() => handleRemoveNewFile(index, fileIndex)}>
                      {fileTexts.remove}
                    </Button>
                  )}
                </div>
              );
            })}

            {!isReadOnly && (
              <div className="flex flex-wrap items-center gap-2">
                <label className="cursor-pointer rounded border border-gray-300 px-3 py-1 text-sm">
                  {fileTexts.addPhotos}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(event) => {
                      handleFilesSelected(index, event.target.files);
                      event.target.value = '';
                    }}
                  />
                </label>
                <Button secondary onClick={() => handleClearAllFiles(index)}>
                  {fileTexts.clearAll}
                </Button>
              </div>
            )}
          </div>
        </div>
      ))}
      {!isReadOnly && (
        <div className="mt-4">
          <Button secondary onClick={handleAddItem}>
            {texts.buttons.addItem}
          </Button>
        </div>
      )}
      </div>
      <CenterScreenModal
        isOpen={viewerState.isOpen}
        onClose={() =>
          setViewerState((prev) => ({
            ...prev,
            isOpen: false,
          }))
        }
      >
        <div className="w-[90vw] max-w-5xl space-y-3">
          <div className="flex h-[70vh] items-center justify-center rounded bg-black p-2">
            {viewerState.urls[viewerState.selectedIndex] && (
              <img
                src={viewerState.urls[viewerState.selectedIndex]}
                alt="Vista ampliada"
                className="max-h-full max-w-full object-contain"
              />
            )}
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {viewerState.urls.map((url, index) => (
              <button
                type="button"
                key={`${url}-${index}`}
                onClick={() =>
                  setViewerState((prev) => ({
                    ...prev,
                    selectedIndex: index,
                  }))
                }
                className={`rounded border-2 ${index === viewerState.selectedIndex ? 'border-blue-600' : 'border-transparent'}`}
              >
                <img src={url} alt={`preview-${index}`} className="h-16 w-16 rounded object-cover" />
              </button>
            ))}
          </div>
        </div>
      </CenterScreenModal>
    </>
  );
};

